/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControlLabel,
  Switch,
  MenuItem,
  Alert,
  Typography,
  Box,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
} from '@mui/icons-material';
import { aiScheduleApi, aiSystemApi } from '../../services/api';
import { useAiDateTimeFormatting } from '../../hooks/useAiDateTimeFormatting';
import type { AISchedule, AIScheduleCreate, ScheduleType, RecurringUnit } from '../../types';

interface ScheduleFormProps {
  open: boolean;
  onClose: () => void;
  schedule?: AISchedule;
}

export const ScheduleForm: React.FC<ScheduleFormProps> = ({ open, onClose, schedule }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const queryClient = useQueryClient();
  const { timezone, toDateTimeLocalValue, fromDateTimeLocalValue } = useAiDateTimeFormatting();
  const isEditing = !!schedule;
  const [isFullscreen, setIsFullscreen] = useState(false);

  const [formData, setFormData] = useState<AIScheduleCreate>({
    ai_system: 0,
    type: 'ONE_TIME',
    start_at: null,
    end_at: null,
    interval_every: null,
    interval_period: null,
    excluded_days: null,
    is_active: true,
  });

  const [error, setError] = useState<string | null>(null);

  // Fetch AI systems for dropdown
  const { data: systemsData } = useQuery({
    queryKey: ['ai-systems'],
    queryFn: () => aiSystemApi.list({}),
  });

  useEffect(() => {
    if (schedule) {
      console.log('Loading schedule for editing:', schedule);
      console.log('interval_every:', schedule.interval_every);
      console.log('interval_period:', schedule.interval_period);
      
      setFormData({
        ai_system: schedule.ai_system,
        type: schedule.type,
        start_at: schedule.start_at,
        end_at: schedule.end_at,
        interval_every: schedule.interval_every,
        interval_period: schedule.interval_period,
        excluded_days: schedule.excluded_days,
        is_active: schedule.is_active,
      });
    } else {
      setFormData({
        ai_system: 0,
        type: 'ONE_TIME',
        start_at: null,
        end_at: null,
        interval_every: null,
        interval_period: null,
        excluded_days: null,
        is_active: true,
      });
    }
    setError(null);
  }, [schedule, open]);

  const handleChange = (field: keyof AIScheduleCreate, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError(null);
  };

  const createMutation = useMutation({
    mutationFn: (data: AIScheduleCreate) => aiScheduleApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
      onClose();
    },
    onError: (err: any) => {
      setError(err.response?.data?.detail || 'Failed to create schedule');
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: AIScheduleCreate) => aiScheduleApi.update(schedule!.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
      onClose();
    },
    onError: (err: any) => {
      setError(err.response?.data?.detail || 'Failed to update schedule');
    },
  });

  const handleSubmit = () => {
    if (!formData.ai_system) {
      setError('Please select an AI system');
      return;
    }

    if (formData.type === 'ONE_TIME' && !formData.start_at) {
      setError('Please specify a start time for one-time schedules');
      return;
    }

    if (formData.type === 'RECURRING' && (!formData.interval_every || !formData.interval_period)) {
      setError('Please specify interval for recurring schedules');
      return;
    }

    if (isEditing) {
      updateMutation.mutate(formData);
    } else {
      createMutation.mutate(formData);
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth={isFullscreen ? false : "sm"} 
      fullWidth 
      fullScreen={isMobile || isFullscreen}
    >
      <DialogTitle sx={{ py: isMobile ? 1.5 : 2 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant={isMobile ? 'subtitle1' : 'h6'}>
            {isEditing ? 'Edit Schedule' : 'Create Schedule'}
          </Typography>
          {!isMobile && (
            <IconButton onClick={() => setIsFullscreen(!isFullscreen)} size="small">
              {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
          )}
        </Box>
      </DialogTitle>
      <DialogContent sx={{ px: isMobile ? 2 : 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={isMobile ? 1.5 : 2} sx={{ mt: 1 }}>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              select
              label="AI System"
              value={formData.ai_system}
              onChange={(e) => handleChange('ai_system', parseInt(e.target.value))}
              required
              disabled={isLoading}
              InputProps={{ style: { fontSize: 16 } }}
            >
              <MenuItem value={0}>Select AI System</MenuItem>
              {systemsData?.results.map((system) => (
                <MenuItem key={system.id} value={system.id}>
                  {system.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              select
              label="Schedule Type"
              value={formData.type}
              onChange={(e) => handleChange('type', e.target.value as ScheduleType)}
              required
              disabled={isLoading}
            >
              <MenuItem value="ONE_TIME">One Time</MenuItem>
              <MenuItem value="RECURRING">Recurring</MenuItem>
            </TextField>
          </Grid>

          {formData.type === 'ONE_TIME' && (
            <>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  type="datetime-local"
                  label="Start At"
                  value={toDateTimeLocalValue(formData.start_at)}
                  onChange={(e) => {
                    if (e.target.value) {
                      handleChange('start_at', fromDateTimeLocalValue(e.target.value));
                    } else {
                      handleChange('start_at', null);
                    }
                  }}
                  required
                  disabled={isLoading}
                  InputLabelProps={{ shrink: true }}
                  helperText={`When to run the schedule (${timezone})`}
                />
              </Grid>
            </>
          )}

          {formData.type === 'RECURRING' && (
            <>
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Recurrence Pattern
                </Typography>
                <Box display="flex" gap={2}>
                  <TextField
                    label="Every"
                    type="number"
                    value={formData.interval_every || ''}
                    onChange={(e) => handleChange('interval_every', parseInt(e.target.value) || null)}
                    required
                    disabled={isLoading}
                    inputProps={{ min: 1 }}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    select
                    label="Period"
                    value={formData.interval_period || ''}
                    onChange={(e) => handleChange('interval_period', e.target.value as RecurringUnit || null)}
                    required
                    disabled={isLoading}
                    sx={{ flex: 2 }}
                    SelectProps={{
                      displayEmpty: true,
                    }}
                  >
                    <MenuItem value="">Select Period</MenuItem>
                    <MenuItem value="seconds">Seconds</MenuItem>
                    <MenuItem value="minutes">Minutes</MenuItem>
                    <MenuItem value="hours">Hours</MenuItem>
                    <MenuItem value="days">Days</MenuItem>
                    <MenuItem value="weeks">Weeks</MenuItem>
                  </TextField>
                </Box>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  type="datetime-local"
                  label="Start At (Optional)"
                  value={toDateTimeLocalValue(formData.start_at)}
                  onChange={(e) => {
                    if (e.target.value) {
                      handleChange('start_at', fromDateTimeLocalValue(e.target.value));
                    } else {
                      handleChange('start_at', null);
                    }
                  }}
                  disabled={isLoading}
                  InputLabelProps={{ shrink: true }}
                  helperText={`When to start the recurring schedule (${timezone})`}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  type="datetime-local"
                  label="End At (Optional)"
                  value={toDateTimeLocalValue(formData.end_at)}
                  onChange={(e) => {
                    if (e.target.value) {
                      handleChange('end_at', fromDateTimeLocalValue(e.target.value));
                    } else {
                      handleChange('end_at', null);
                    }
                  }}
                  disabled={isLoading}
                  InputLabelProps={{ shrink: true }}
                  helperText={`When to stop the recurring schedule (${timezone})`}
                />
              </Grid>
            </>
          )}

          <Grid size={{ xs: 12 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => handleChange('is_active', e.target.checked)}
                  disabled={isLoading}
                />
              }
              label="Active"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ 
        px: isMobile ? 2 : 3, 
        py: isMobile ? 1.5 : 2,
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 1 : 0,
        '& > button': { width: isMobile ? '100%' : 'auto' }
      }}>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isLoading}
        >
          {isEditing ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
